'use client'

import { useEffect, useMemo, useState } from 'react'
import { useTranslations } from 'next-intl'
import clsx from 'clsx'
import { ActionList, ActionListChildren, Button, Toaster } from '@node-space/ui-components'
import { ellipseMiddleText, floorNumber, numberWithCommas } from '@node-space/utils'
import { useModalContext } from '@/app/context/ModalContext'
import { useProtocolContext } from '@/app/context/ProtocolContext'
import useRate from '@/app/hooks/mutations/useRate'
import { useLaunchDarkly } from '@/app/hooks/useLaunchDarkly'
import { useMeshConnect } from '@/app/hooks/useMeshConnect'
import useTrackEvent from '@/app/hooks/useTrackEvent'
import isEmpty from 'lodash/isEmpty'
import pick from 'lodash/pick'
import { useTheme } from 'next-themes'
import { Channel } from '@/types/channel'
import { Currency } from '@/types/currency'
import { LdUser } from '@/types/ldUser'
import { MeshSettings } from '@/types/mesh'
import { Flow } from '@/types/payment'
import { WalletTransaction } from '@/types/wallet'
import { CHANNEL_API_CALL_INTERVAL } from '@/utils/constants'
import { getCurrencyPrecision } from '@/utils/helpers'
import { QRCode } from '../../component-library/QRCode/QRCode'
import ModalMobile from '../modal/ModalMobile'
import { ChangeProtocolButton } from '../pay/ChangeProtocolButton'
import { GenericPaymentDisclaimer } from '../pay/GenericPaymentDisclaimer'
import PaymentDisclaimer from '../pay/PaymentDisclaimer'
import { PaymentSelectProtocol } from '../pay/PaymentSelectProtocol'
import { WalletIconsStack } from '../web3/WalletIconsStack'
import { ChannelFooter } from './ChannelFooter'

interface ChannelDetailsProps {
  channel: Channel
  currencies: Currency[]
  isMobile: boolean
  merchantDisplayName: string
  ldUser: LdUser
  meshPaymentTransaction: WalletTransaction
  isMeshEnabled: boolean
  meshSettings?: MeshSettings
}

const ChannelDetails = ({
  channel,
  currencies,
  isMobile,
  merchantDisplayName,
  ldUser,
  meshPaymentTransaction,
  isMeshEnabled,
  meshSettings,
}: ChannelDetailsProps) => {
  const t = useTranslations()
  const { protocol } = useProtocolContext()
  const track = useTrackEvent()
  const { isOpen, closeModal } = useModalContext()
  const [showProtocolModal, setShowProtocolModal] = useState(true)
  const { openMeshLink, isFetchingToken } = useMeshConnect(meshPaymentTransaction, meshSettings)
  const { resolvedTheme } = useTheme()
  const isPegged = channel?.pegged ?? false
  const { rate, isFetching } = useRate(channel?.uuid, isPegged ? false : CHANNEL_API_CALL_INTERVAL)
  const [selectedCurrencyCode] = useState(channel?.payCurrency?.toLowerCase())
  const isMultiProtocolCurrency = !isEmpty(channel?.alternatives)
  const counterCurrency = rate?.display?.counter || ''
  const counterRate = isPegged
    ? 1
    : floorNumber(rate?.display?.rate ?? 0, getCurrencyPrecision(counterCurrency, currencies))

  const paymentSummaryProtocol = channel?.protocol
  const paymentSummaryAlternativeProtocols = channel?.alternatives?.map(({ protocol }) => protocol)

  const paymentSummaryProtocols = [paymentSummaryProtocol, ...paymentSummaryAlternativeProtocols]

  useLaunchDarkly({ ldUser })

  useEffect(() => {
    track('Channel Details Page Viewed', {
      category: 'Hosted Payments Page',
      payment_id: channel?.uuid,
      version: 2.0,
      flow: Flow.CHANNEL,
      status: 'N/A',
      merchantName: merchantDisplayName,
      reference: channel?.reference,
      theme: resolvedTheme,
    })
  }, [])

  const displayAddress = useMemo(() => {
    if (!isMultiProtocolCurrency) {
      return channel?.address
    }

    const currentAddressWithProtocol = pick(channel, ['protocol', 'address'])

    const addressAndProtocolArray = [currentAddressWithProtocol, ...channel.alternatives]

    const extractedAddress = addressAndProtocolArray.find(
      alternative => alternative.protocol.toLowerCase() === protocol
    )

    return extractedAddress?.address ?? channel?.address
  }, [isMultiProtocolCurrency, channel.address, protocol])

  const renderQRCodeMobile = (
    <div className="flex flex-col items-center gap-3 sm:px-6">
      <QRCode
        displayAddress={displayAddress}
        selectedCurrencyCode={selectedCurrencyCode}
        protocol={protocol}
        size={120}
        isLoading={rate == undefined}
      />
      <div>
        <p className="text-foreground-prominent text-center font-medium">
          {t('qrCodeMobileMessage')}
        </p>
      </div>
    </div>
  )

  const displayProtocol =
    isMultiProtocolCurrency && !isMeshEnabled ? `(${protocol?.toUpperCase()})` : ''

  const displaySubTitleRate = rate
    ? `1 ${channel?.payCurrency} ${displayProtocol} = ${numberWithCommas(counterRate)} ${
        counterCurrency
      }`
    : ''

  const paymentDetailsInfoItems: ActionListChildren[] = [
    {
      id: 'infoTileRate',
      title: t('indicativeExchangeRate'),
      BodyPre: displaySubTitleRate,
      className: clsx('bg-background-card rounded', rate == undefined && 'pb-6 animate-pulse'),
    },
  ]

  if (!isMeshEnabled) {
    paymentDetailsInfoItems.unshift({
      id: 'infoTileAddress',
      title: t('sendToThisAddress', {
        protocol: displayProtocol,
        currencyCode: channel?.payCurrency,
      }),
      BodyPre: `${ellipseMiddleText(displayAddress, 6, 6)}`,
      copyText: displayAddress,
      className: 'bg-background-card rounded mb-3',
    })
  }

  if (channel?.tag) {
    paymentDetailsInfoItems.push({
      id: 'infoTileTag',
      title: t('destinationTag'),
      BodyPre: channel.tag,
      copyText: channel.tag,
      className: 'bg-background-card rounded mt-3',
    })
  }

  return (
    <>
      {!isMeshEnabled && (
        <PaymentSelectProtocol
          paymentSummaryProtocols={paymentSummaryProtocols}
          paymentProtocol={channel?.protocol}
          isMultiProtocolCurrency={isMultiProtocolCurrency}
          currencies={currencies}
          selectedCurrencyCode={selectedCurrencyCode}
          isMobile={isMobile}
          showProtocolModal={showProtocolModal}
          setShowProtocolModal={setShowProtocolModal}
        />
      )}

      <div className="flex flex-col items-center gap-4 pb-3 sm:flex sm:flex-row sm:items-center">
        {!isMobile && !isMeshEnabled && (
          <div className={clsx('p-1', channel?.tag && 'sm:pb-20')}>
            <QRCode
              displayAddress={displayAddress}
              selectedCurrencyCode={selectedCurrencyCode}
              protocol={protocol}
              isLoading={rate == undefined}
            />
          </div>
        )}

        <div className="flex w-full flex-col gap-4">
          <ActionList id="channelDetailsInfo" border={false} items={paymentDetailsInfoItems} />
        </div>
      </div>

      {isMeshEnabled ? (
        <div className="flex flex-col gap-3">
          <Button
            full
            iconElement={<WalletIconsStack shouldShowWeb3Wallets={false} />}
            iconPosition="right"
            onClick={() => openMeshLink()}
            loading={isFetchingToken}
          >
            {t('payWithWallet')}
          </Button>
          <GenericPaymentDisclaimer />
        </div>
      ) : (
        <>
          <PaymentDisclaimer
            selectedProtocol={protocol}
            isMultiProtocolCurrency={isMultiProtocolCurrency}
            selectedCurrencyCode={selectedCurrencyCode}
            currencies={currencies}
          />
          <ChangeProtocolButton
            isMultiProtocolCurrency={isMultiProtocolCurrency}
            onClick={() => setShowProtocolModal(!showProtocolModal)}
          />
        </>
      )}

      {!isPegged && <ChannelFooter isFetching={isFetching} />}
      {isMobile && (
        <ModalMobile
          disabled={false}
          isOpen={isOpen}
          onClose={closeModal}
          body={renderQRCodeMobile}
        />
      )}

      <Toaster position={isMobile ? 'top-center' : 'top-right'} visibleToasts={5} />
    </>
  )
}

export default ChannelDetails
